import { useState, useEffect, useMemo, useRef, type FormEvent, type ReactNode } from 'react';
import QRCode from 'qrcode';
import { useLocalParticipant, useParticipants } from '@livekit/components-react';
import { Track } from 'livekit-client';
import { ChatPanel } from '../chat/ChatPanel';
import { useRoom } from './RoomProvider';
import { useAuth } from '../../auth';
import { applyArtworkFavoriteState } from './artworkFavorites';
import { canAccessSidebarChat } from './sidebarChatAvailability';
import { authHeaders } from '../../shared/authHeaders';
import type { ChatMessage, SessionArtwork } from '@modelcast/types';

export type SidebarTab = 'schedule' | 'participants' | 'chat' | 'gallery' | 'controls';

interface SidebarPanelProps {
  roomCode: string;
  participantId: string;
  artistProfileId?: string;
  artistName?: string;
  onUpload: () => void;
  onPinArtwork: (artwork: SessionArtwork) => void;
  canPinArtwork?: boolean;
  onSelectParticipant?: (identity: string | null) => void;
  pinnedParticipant?: string | null;
  scheduleContent?: ReactNode;
  controlsContent?: ReactNode;
  initialTab?: SidebarTab;
  tabs?: SidebarTab[];
  className?: string;
  dockChatInputOnMobile?: boolean;
}

const tabLabels: Record<SidebarTab, string> = {
  schedule: 'Poses',
  participants: 'Participants',
  chat: 'Chat',
  gallery: 'Gallery',
  controls: 'Session',
};

const defaultTabs: SidebarTab[] = ['chat', 'gallery'];

export function SidebarPanel({
  roomCode,
  participantId,
  artistProfileId,
  artistName,
  onUpload,
  onPinArtwork,
  canPinArtwork = false,
  onSelectParticipant,
  pinnedParticipant,
  scheduleContent,
  controlsContent,
  initialTab = 'chat',
  tabs = defaultTabs,
  className = 'h-full',
  dockChatInputOnMobile = false,
}: SidebarPanelProps) {
  const [activeTab, setActiveTab] = useState<SidebarTab>(initialTab);
  const [artwork, setArtwork] = useState<SessionArtwork[]>([]);
  const [unreadChats, setUnreadChats] = useState(0);
  const [unseenArt, setUnseenArt] = useState(0);
  const [loading, setLoading] = useState(true);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const {
    socket,
    isHost,
    participants: sessionParticipants,
    startedAt,
    galleryArtworkFavorites,
  } = useRoom();
  const activeTabRef = useRef(activeTab);
  const isGalleryAvailable = Boolean(startedAt);
  const isChatAvailable = canAccessSidebarChat({
    startedAt,
    isHost,
    participantId,
    participants: sessionParticipants,
  });
  const participantCount = useMemo(
    () => getUniqueUserCount(sessionParticipants),
    [sessionParticipants]
  );
  const sessionUrl = useMemo(() => getSessionShareUrl(roomCode), [roomCode]);
  const artworkWithFavoriteState = useMemo(
    () => artwork.map((item) => applyArtworkFavoriteState(item, galleryArtworkFavorites, participantId)),
    [artwork, galleryArtworkFavorites, participantId]
  );
  const visibleTabs = useMemo(
    () => tabs.filter((tab) => {
      if (tab === 'schedule') return scheduleContent;
      if (tab === 'controls') return controlsContent;
      return true;
    }),
    [controlsContent, scheduleContent, tabs]
  );

  useEffect(() => {
    if (!visibleTabs.includes(activeTab)) {
      setActiveTab(visibleTabs[0] ?? 'chat');
    }
  }, [activeTab, visibleTabs]);

  useEffect(() => {
    activeTabRef.current = activeTab;
    if (activeTab === 'chat') {
      setUnreadChats(0);
    }
    if (activeTab === 'gallery') {
      setUnseenArt(0);
    }
  }, [activeTab]);

  // Fetch artwork on mount
  useEffect(() => {
    if (!isGalleryAvailable) {
      setArtwork([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    fetchArtwork();
  }, [isGalleryAvailable, roomCode]);

  // Listen for new artwork uploads
  useEffect(() => {
    if (!socket || !isGalleryAvailable) return;

    socket.on('gallery:new-artwork', (newArtwork: SessionArtwork) => {
      setArtwork((prev) => [newArtwork, ...prev]);
      if (activeTabRef.current !== 'gallery') {
        setUnseenArt(count => count + 1);
      }
    });

    return () => {
      socket.off('gallery:new-artwork');
    };
  }, [isGalleryAvailable, socket]);

  useEffect(() => {
    if (!socket) return;

    const handleChatMessage = (message: ChatMessage) => {
      if (!isChatAvailable) return;

      if (activeTabRef.current !== 'chat' && message.participantId !== participantId) {
        setUnreadChats(count => count + 1);
      }
    };

    const handleChatCleared = () => {
      setUnreadChats(0);
    };

    socket.on('chat:message', handleChatMessage);
    socket.on('chat:cleared', handleChatCleared);

    return () => {
      socket.off('chat:message', handleChatMessage);
      socket.off('chat:cleared', handleChatCleared);
    };
  }, [isChatAvailable, participantId, socket]);

  const fetchArtwork = async () => {
    try {
      const response = await fetch(`/api/rooms/${roomCode}/artwork`, {
        headers: {
          'x-participant-id': participantId,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setArtwork(data.artwork || []);
      }
    } catch (error) {
      console.error('Error fetching artwork:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async (artworkId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const response = await fetch(`/api/rooms/${roomCode}/artwork/${artworkId}/like`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ participantId }),
      });

      if (response.ok) {
        await response.json().catch(() => null);
      }
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };

  const getTabBadge = (tab: SidebarTab) => {
    if (tab === 'participants') return participantCount;
    if (tab === 'chat') return isChatAvailable ? unreadChats : 0;
    if (tab === 'gallery') return isGalleryAvailable ? unseenArt : 0;
    return 0;
  };

  return (
    <div className={`${className} flex flex-col`}>
      <div className="flex flex-shrink-0 items-center justify-between gap-3 border-b border-line-soft px-3 py-3">
        <a
          href={sessionUrl}
          target="_blank"
          rel="noreferrer"
          title={sessionUrl}
          className="min-w-0 flex-1 truncate rounded border border-line bg-panel px-2.5 py-1.5 font-mono text-[11px] text-text-dim transition-colors hover:border-text/30 hover:text-text"
        >
          {sessionUrl}
        </a>
        <button
          type="button"
          onClick={() => setInviteModalOpen(true)}
          className="btn btn-sm flex-shrink-0"
        >
          Invite others
        </button>
      </div>

      {/* Tab bar */}
      <div className="flex flex-shrink-0 overflow-x-auto overflow-y-hidden border-b border-line-soft [-webkit-overflow-scrolling:touch]">
        {visibleTabs.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`min-w-max flex-none px-4 py-3.5 text-[14px] font-semibold transition-colors flex items-center justify-center gap-2 ${
              activeTab === tab
                ? 'text-text border-b-2 border-accent -mb-px'
                : 'text-text-dim hover:text-text'
            }`}
          >
            <span className="truncate">{tabLabels[tab]}</span>
            {getTabBadge(tab) > 0 && (
              <span className={`min-w-[22px] h-[22px] rounded-full px-1.5 text-[11px] font-semibold flex items-center justify-center ${
                activeTab === tab ? 'bg-accent text-white' : 'bg-accent/10 text-accent'
              }`}>
                {getTabBadge(tab)}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div
        className={`flex flex-col ${
          activeTab === 'schedule'
            ? 'flex-none'
            : 'flex-1 min-h-[360px] max-h-[70svh] lg:min-h-0 lg:max-h-none'
        }`}
      >
        {activeTab === 'schedule' ? (
          scheduleContent
        ) : activeTab === 'controls' ? (
          controlsContent
        ) : activeTab === 'participants' ? (
          <ParticipantsTabContent
            onSelectParticipant={onSelectParticipant}
            pinnedParticipant={pinnedParticipant}
          />
        ) : activeTab === 'chat' && !isChatAvailable ? (
          <ChatUnavailableMessage />
        ) : activeTab === 'chat' ? (
          <ChatPanel dockInputOnMobile={dockChatInputOnMobile} />
        ) : !isGalleryAvailable ? (
          <GalleryUnavailableMessage />
        ) : (
          <GalleryTabContent
            roomCode={roomCode}
            participantId={participantId}
            artistProfileId={artistProfileId}
            artistName={artistName}
            artwork={artworkWithFavoriteState}
            loading={loading}
            onUpload={onUpload}
            onPinArtwork={onPinArtwork}
            canPinArtwork={canPinArtwork}
            onLike={handleLike}
          />
        )}
      </div>

      {inviteModalOpen && (
        <InviteOthersModal
          roomCode={roomCode}
          onClose={() => setInviteModalOpen(false)}
        />
      )}
    </div>
  );
}

function getSessionShareUrl(roomCode: string) {
  if (typeof window === 'undefined') return `/s/${roomCode}`;
  return new URL(`/s/${roomCode}`, window.location.origin).toString();
}

function InviteOthersModal({
  roomCode,
  onClose,
}: {
  roomCode: string;
  onClose: () => void;
}) {
  const [email, setEmail] = useState('');
  const [copied, setCopied] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sendStatus, setSendStatus] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const sessionUrl = useMemo(() => getSessionShareUrl(roomCode), [roomCode]);

  const copySessionUrl = async () => {
    try {
      await navigator.clipboard.writeText(sessionUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch (error) {
      console.error('Failed to copy session link:', error);
    }
  };

  const sendInviteEmails = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const emails = Array.from(new Set(email
      .split(',')
      .map((address) => address.trim().toLowerCase())
      .filter(Boolean)));
    if (emails.length === 0 || isSending) return;

    const invalidEmail = emails.find((address) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address));
    if (invalidEmail) {
      setSendStatus(null);
      setSendError(`Check this email address: ${invalidEmail}`);
      return;
    }

    setIsSending(true);
    setSendStatus(null);
    setSendError(null);

    let sent = 0;
    const failed: string[] = [];

    for (const targetEmail of emails) {
      try {
        const response = await fetch(`/api/rooms/${roomCode}/invites`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: targetEmail, role: 'artist' }),
        });

        if (!response.ok) {
          failed.push(targetEmail);
          continue;
        }

        const result = await response.json();
        if (result.emailSent) {
          sent += 1;
        } else {
          failed.push(targetEmail);
        }
      } catch (error) {
        console.error('Failed to send invite email:', error);
        failed.push(targetEmail);
      }
    }

    setIsSending(false);

    if (failed.length > 0) {
      setSendError(`Could not send ${failed.length} invite${failed.length === 1 ? '' : 's'}.`);
    }

    if (sent > 0) {
      setSendStatus(`${sent} invite email${sent === 1 ? '' : 's'} sent.`);
      if (failed.length === 0) {
        setEmail('');
      }
    } else if (failed.length === 0) {
      setSendError('No invite emails were sent.');
    }
  };

  const inviteButtonLabel = email.includes(',') ? 'Send invites' : 'Send invite';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 px-4 py-6" onClick={onClose}>
      <div
        className="w-full max-w-md overflow-hidden rounded-xl border border-line bg-panel shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
          <div>
            <h2 className="font-display text-xl font-medium text-text">Invite others</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-text-dim transition-colors hover:bg-panel2 hover:text-text"
            aria-label="Close invite modal"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-5 px-5 py-5">
          <form onSubmit={sendInviteEmails} className="space-y-2">
            <label htmlFor="session-invite-email" className="block text-[11px] font-semibold uppercase tracking-[0.08em] text-text-faint">
              Email addresses
            </label>
            <div className="flex flex-col gap-2 sm:flex-row">
              <input
                id="session-invite-email"
                type="text"
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                  setSendStatus(null);
                  setSendError(null);
                }}
                placeholder="friend@example.com, alex@example.com"
                className="input min-w-0 flex-1"
                required
              />
              <button type="submit" className="btn btn-accent flex-shrink-0" disabled={isSending || !email.trim()}>
                {isSending ? 'Sending...' : inviteButtonLabel}
              </button>
            </div>
            {sendStatus && (
              <p className="text-sm text-ok">{sendStatus}</p>
            )}
            {sendError && (
              <p className="text-sm text-warn">{sendError}</p>
            )}
          </form>

          <div className="space-y-2">
            <label htmlFor="session-share-url" className="block text-[11px] font-semibold uppercase tracking-[0.08em] text-text-faint">
              Session link
            </label>
            <div className="flex flex-col gap-2 sm:flex-row">
              <input
                id="session-share-url"
                type="text"
                value={sessionUrl}
                readOnly
                className="input min-w-0 flex-1 font-mono text-[12px]"
                onFocus={(event) => event.currentTarget.select()}
              />
              <button type="button" onClick={copySessionUrl} className="btn flex-shrink-0">
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ChatUnavailableMessage() {
  return (
    <div className="flex min-h-[320px] flex-1 items-center justify-center p-6 text-center">
      <div className="max-w-[18rem] rounded-xl border border-line bg-panel2/60 px-5 py-6 shadow-sm">
        <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-panel">
          <svg className="h-5 w-5 text-text-faint" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
          </svg>
        </div>
        <p className="text-sm font-medium leading-snug text-text">
          Chat will be available when the session has been opened
        </p>
      </div>
    </div>
  );
}

function GalleryUnavailableMessage() {
  return (
    <div className="flex min-h-[320px] flex-1 items-center justify-center p-6 text-center">
      <div className="max-w-[18rem] rounded-xl border border-line bg-panel2/60 px-5 py-6 shadow-sm">
        <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-panel">
          <svg className="h-5 w-5 text-text-faint" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="16" rx="2" />
            <path d="M3 15l5-4 4 3 5-5 4 3" />
            <path d="M8 9h.01" />
          </svg>
        </div>
        <p className="text-sm font-medium leading-snug text-text">
          Gallery will be available when the session has been opened
        </p>
      </div>
    </div>
  );
}

function getUniqueUserCount(participants: { participantId: string; profileId: string | null }[]) {
  const userIds = new Set<string>();

  participants.forEach((participant) => {
    userIds.add(participant.profileId || participant.participantId);
  });

  return userIds.size;
}

function ParticipantsTabContent({
  onSelectParticipant,
  pinnedParticipant,
}: {
  onSelectParticipant?: (identity: string | null) => void;
  pinnedParticipant?: string | null;
}) {
  const participants = useParticipants();
  const { localParticipant } = useLocalParticipant();
  const {
    modelParticipantIds,
    isHost: viewerIsHost,
    hostParticipantIds,
    originalHostId,
    socket,
    participants: sessionParticipants,
    stagePinnedParticipantId,
    pinStageParticipant,
    clearStagePinnedParticipant,
    banParticipant,
  } = useRoom();
  const [selectedParticipantId, setSelectedParticipantId] = useState<string | null>(null);
  const admittedParticipants = participants;
  const sessionParticipantById = useMemo(() => (
    new Map(sessionParticipants.map(participant => [participant.participantId, participant]))
  ), [sessionParticipants]);
  const getParticipantDisplayName = (participant: typeof participants[0] | undefined) => {
    if (!participant) return '';
    const sessionDisplayName = sessionParticipantById.get(participant.identity)?.displayName?.trim();
    return sessionDisplayName || participant.name || participant.identity;
  };
  const selectedParticipant = admittedParticipants.find(p => p.identity === selectedParticipantId);
  const selectedDisplayName = getParticipantDisplayName(selectedParticipant);
  const modelIdSet = new Set([
    ...modelParticipantIds,
    ...sessionParticipants
      .filter(participant => participant.role === 'model')
      .map(participant => participant.participantId),
  ]);
  const isCurrentUserModel = !!localParticipant?.identity && modelIdSet.has(localParticipant.identity);
  const canBan = viewerIsHost || isCurrentUserModel;
  const canModerateParticipantAudio = viewerIsHost || isCurrentUserModel;

  const closeMenu = () => setSelectedParticipantId(null);

  const isParticipantMicrophoneMuted = (participant: typeof participants[0]) => {
    const microphonePublication = participant.getTrackPublication(Track.Source.Microphone);
    return participant.isMicrophoneEnabled === false || !microphonePublication || microphonePublication.isMuted;
  };

  const togglePin = () => {
    if (!selectedParticipantId) return;

    onSelectParticipant?.(pinnedParticipant === selectedParticipantId ? null : selectedParticipantId);
    closeMenu();
  };

  const toggleSpotlight = () => {
    if (!selectedParticipantId) return;

    if (stagePinnedParticipantId === selectedParticipantId) {
      clearStagePinnedParticipant();
    } else {
      pinStageParticipant(selectedParticipantId);
    }

    closeMenu();
  };

  const toggleMute = () => {
    if (!selectedParticipantId) return;

    const participant = participants.find(p => p.identity === selectedParticipantId);
    if (!participant || !canModerateParticipantAudio) {
      closeMenu();
      return;
    }

    if (!socket) {
      alert('Unable to update participant microphone right now');
      closeMenu();
      return;
    }

    socket.emit('room:mute-participant', selectedParticipantId, (result) => {
      if (!result.success) {
        alert(result.error || 'Failed to update participant microphone');
      }
    });
    closeMenu();
  };

  // Remove the participant and block their account from this event.
  // The optional note is stored privately for the moderation record.
  const removeAndBlockParticipant = () => {
    if (!selectedParticipantId) return;

    const reason = prompt(
      `Remove ${selectedDisplayName} and block them from this event? They cannot re-enter unless you allow them back in.\n\nOptional private note for the moderation record (never shown to them):`
    );
    if (reason === null) {
      closeMenu();
      return;
    }

    banParticipant(selectedParticipantId, reason.trim() || null, (result) => {
      if (!result.success) {
        alert(result.error || 'Failed to remove participant');
      }
    });

    closeMenu();
  };

  const canBanSelected =
    !!selectedParticipantId &&
    canBan &&
    selectedParticipantId !== originalHostId &&
    selectedParticipantId !== localParticipant?.identity;
  const isSelectedLocal = selectedParticipantId === localParticipant?.identity;
  const isSelectedMuted = selectedParticipant
    ? isParticipantMicrophoneMuted(selectedParticipant)
    : false;

  if (admittedParticipants.length === 0) {
    return (
      <div className="min-h-[260px] flex items-center justify-center p-6 text-sm text-text-faint">
        No participants yet
      </div>
    );
  }

  return (
    <div className="min-h-[320px] max-h-[70svh] overflow-y-auto">
      <div className="divide-y divide-line-soft">
        {admittedParticipants.map((participant) => {
          const isLocal = participant.identity === localParticipant?.identity;
          const isModel = modelIdSet.has(participant.identity);
          const isHost = hostParticipantIds.includes(participant.identity);
          const isOrganizerPinned = stagePinnedParticipantId === participant.identity;
          const isLocalPinned = pinnedParticipant === participant.identity;
          const isPinned = isLocalPinned || (!pinnedParticipant && isOrganizerPinned);
          const displayName = getParticipantDisplayName(participant);
          const initials = displayName.slice(0, 1).toUpperCase();

          return (
            <button
              key={participant.identity}
              type="button"
              onClick={() => setSelectedParticipantId(participant.identity)}
              className={`w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-panel2 transition-colors ${
                isPinned ? 'bg-accent/5' : ''
              }`}
            >
              <div className="w-9 h-9 rounded-full bg-accent/12 border border-accent/20 flex items-center justify-center text-[13px] font-semibold text-accent flex-shrink-0">
                {initials || '?'}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-medium text-text truncate">
                  {isLocal ? `${displayName} (You)` : displayName}
                </div>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {isModel && (
                    <span className="px-1.5 py-0.5 rounded bg-tag/10 text-[9px] font-semibold uppercase text-tag">
                      Model
                    </span>
                  )}
                  {isHost && (
                    <span className="px-1.5 py-0.5 rounded bg-accent/10 text-[9px] font-semibold uppercase text-accent">
                      Host
                    </span>
                  )}
                  {isPinned && (
                    <span className="px-1.5 py-0.5 rounded bg-panel border border-line text-[9px] font-semibold uppercase text-text-dim">
                      {isLocalPinned ? 'Pinned' : 'Spotlight'}
                    </span>
                  )}
                  {!isModel && !isHost && (
                    <span className="text-[10px] text-text-faint">
                      Artist
                    </span>
                  )}
                </div>
              </div>
              <span className="text-[18px] leading-none text-text-faint">
                ...
              </span>
            </button>
          );
        })}
      </div>
      {selectedParticipant && (
        <div className="fixed inset-0 z-50 flex items-end bg-black/30 lg:hidden" onClick={closeMenu}>
          <div
            className="w-full rounded-t-xl border border-line bg-panel shadow-xl pb-[env(safe-area-inset-bottom)]"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-line">
              <div className="text-[14px] font-semibold text-text truncate">
                {isSelectedLocal ? 'You' : selectedDisplayName}
              </div>
            </div>

            <button
              onClick={togglePin}
              className="w-full px-4 py-3.5 text-left text-[15px] hover:bg-panel2 transition-colors flex items-center gap-3"
            >
              {pinnedParticipant === selectedParticipantId ? (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                  <span>Unpin</span>
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
                  </svg>
                  <span>Pin</span>
                </>
              )}
            </button>

            {viewerIsHost && (
              <button
                onClick={toggleSpotlight}
                className="w-full px-4 py-3.5 text-left text-[15px] hover:bg-panel2 transition-colors flex items-center gap-3"
              >
                {stagePinnedParticipantId === selectedParticipantId ? (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                      <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                    <span>Clear spotlight</span>
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                      <path d="M12 3l2.8 5.7 6.2.9-4.5 4.4 1.1 6.2L12 17.2 6.4 20.2 7.5 14 3 9.6l6.2-.9L12 3z" />
                    </svg>
                    <span>Spotlight</span>
                  </>
                )}
              </button>
            )}

            {!isSelectedLocal && canModerateParticipantAudio && !isSelectedMuted && (
              <button
                onClick={toggleMute}
                className="w-full px-4 py-3.5 text-left text-[15px] hover:bg-panel2 transition-colors flex items-center gap-3"
              >
                <svg className="w-5 h-5 text-warn" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                  <path d="M11 5L6 9H2v6h4l5 4V5z" />
                  <line x1="23" y1="9" x2="17" y2="15" />
                  <line x1="17" y1="9" x2="23" y2="15" />
                </svg>
                <span>Mute microphone</span>
              </button>
            )}

            {canBanSelected && (
              <>
                <div className="border-t border-line my-1" />
                <button
                  onClick={removeAndBlockParticipant}
                  className="w-full px-4 py-3.5 text-left text-[15px] hover:bg-warn/10 transition-colors flex items-center gap-3 text-warn"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                    <path d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  <span>Remove and block from this event</span>
                </button>
              </>
            )}

            <button
              onClick={closeMenu}
              className="w-full px-4 py-3.5 text-center text-[15px] text-text-dim border-t border-line hover:bg-panel2 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

interface GalleryTabContentProps {
  roomCode: string;
  participantId: string;
  artistProfileId?: string;
  artistName?: string;
  artwork: SessionArtwork[];
  loading: boolean;
  onUpload: () => void;
  onPinArtwork: (artwork: SessionArtwork) => void;
  canPinArtwork: boolean;
  onLike: (artworkId: string, e: React.MouseEvent) => void;
}

function GalleryTabContent({ roomCode, participantId, artistProfileId, artistName, artwork, loading, onUpload, onPinArtwork, canPinArtwork, onLike }: GalleryTabContentProps) {
  const { getToken, isLoaded: authLoaded, isSignedIn } = useAuth();
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string | null>(null);
  const [lightboxArtwork, setLightboxArtwork] = useState<SessionArtwork | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function generateQrCode() {
      const uploadUrl = new URL(`/s/${roomCode}/upload`, window.location.origin);
      if (artistProfileId) {
        uploadUrl.searchParams.set('artistProfileId', artistProfileId);
      }
      if (artistName) {
        uploadUrl.searchParams.set('artistName', artistName);
      }

      if (authLoaded && isSignedIn) {
        try {
          const token = await getToken();
          const response = await fetch(
            `/api/rooms/${roomCode}/artwork/upload-token?participantId=${encodeURIComponent(participantId)}`,
            {
              headers: authHeaders(token),
            }
          );
          if (response.ok) {
            const data = await response.json();
            if (typeof data.uploadToken === 'string' && data.uploadToken) {
              uploadUrl.searchParams.set('uploadToken', data.uploadToken);
            }
          } else {
            console.warn('[GalleryUploadQR] Upload token unavailable', { status: response.status });
          }
        } catch (error) {
          console.warn('[GalleryUploadQR] Failed to create upload token', error);
        }
      }

      const dataUrl = await QRCode.toDataURL(uploadUrl.toString(), {
        width: 160,
        margin: 1,
        color: {
          dark: '#1c1714',
          light: '#ffffff',
        },
      });

      if (!cancelled) {
        setQrCodeDataUrl(dataUrl);
      }
    }

    setQrCodeDataUrl(null);
    generateQrCode().catch(console.error);

    return () => {
      cancelled = true;
    };
  }, [artistName, artistProfileId, authLoaded, getToken, isSignedIn, participantId, roomCode]);

  useEffect(() => {
    if (!lightboxArtwork) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setLightboxArtwork(null);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [lightboxArtwork]);

  // QR Code header - always shown
  const QRHeader = (
    <div className="p-3 border-b border-line-soft bg-panel2/50">
      <div className="flex items-center gap-3">
        {/* QR Code */}
        <div className="w-[72px] h-[72px] bg-white p-1 rounded-md flex-shrink-0 shadow-sm">
          {qrCodeDataUrl ? (
            <img src={qrCodeDataUrl} alt="Scan to upload artwork" className="w-full h-full" />
          ) : (
            <div className="w-full h-full bg-panel2 animate-pulse rounded" />
          )}
        </div>

        {/* Instructions */}
        <div className="flex-1 min-w-0">
          <p className="font-display text-[13px] text-text font-medium">Share Your Work</p>
          <button
            onClick={onUpload}
            className="mt-2 btn btn-sm inline-flex items-center justify-center gap-2"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M12 5v14M5 12h14"/>
            </svg>
            Upload
          </button>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex-1 flex flex-col">
        {QRHeader}
        <div className="flex-1 flex items-center justify-center">
          <span className="text-text-dim text-sm">Loading gallery...</span>
        </div>
      </div>
    );
  }

  if (artwork.length === 0) {
    return (
      <div className="flex-1 flex flex-col">
        {QRHeader}
        <div className="flex-1 flex flex-col items-center justify-center gap-3 p-6">
          <div className="w-12 h-12 rounded-full bg-panel2 flex items-center justify-center">
            <svg className="w-6 h-6 text-text-faint" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="3" y="4" width="18" height="16" rx="2"/>
              <path d="M3 15l5-4 4 3 5-5 4 3"/>
              <circle cx="8" cy="9" r="1.5"/>
            </svg>
          </div>
          <div className="text-center">
            <p className="text-sm text-text-dim">No artwork yet</p>
            <p className="text-xs text-text-faint mt-1">Be the first to share!</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto flex flex-col">
      {QRHeader}

      {/* Artwork grid */}
      <div className="p-3 space-y-3">
        {artwork.map((item) => (
          <div
            key={item.id}
            className="w-full text-left bg-panel2 rounded-lg overflow-hidden border border-line transition-colors group hover:border-accent/50"
          >
            {/* Image */}
            <button
              type="button"
              onClick={() => setLightboxArtwork(item)}
              className="relative block aspect-[4/3] w-full bg-[#1c1714]"
              aria-label={`Open artwork by ${item.artistProfile?.displayName || item.participantName}`}
            >
              <img
                src={item.imageUrl}
                alt={`Artwork by ${item.participantName}`}
                className="absolute inset-0 w-full h-full object-contain"
              />
              {/* View overlay on hover */}
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <span className="text-white text-xs font-medium bg-black/60 px-3 py-1.5 rounded-full">
                  View
                </span>
              </div>
            </button>

            {/* Info */}
            <div className="p-2.5 flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="text-[12px] font-medium text-text truncate">
                  {item.artistProfile?.displayName || item.participantName}
                </div>
                {item.medium && (
                  <div className="text-[10px] text-text-faint truncate">
                    {item.medium}
                  </div>
                )}
              </div>

              <div className="flex flex-shrink-0 items-center gap-1.5">
                {canPinArtwork && (
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      onPinArtwork(item);
                    }}
                    className="rounded px-2 py-1 text-[11px] font-medium text-text-dim transition-colors hover:bg-accent/10 hover:text-accent"
                    aria-label={`Spotlight artwork by ${item.artistProfile?.displayName || item.participantName}`}
                  >
                    Spotlight
                  </button>
                )}
                {/* Like button */}
                <button
                  type="button"
                  onClick={(e) => onLike(item.id, e)}
                  className={`flex items-center gap-1 text-[11px] px-2 py-1 rounded transition-colors ${
                    item.isLikedByMe
                      ? 'text-accent bg-accent/10'
                      : 'text-text-dim hover:text-accent hover:bg-accent/5'
                  }`}
                  aria-label={`Like artwork by ${item.artistProfile?.displayName || item.participantName}`}
                >
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill={item.isLikedByMe ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                  </svg>
                  {item.likesCount || 0}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {lightboxArtwork && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/82 px-4 py-6"
          role="dialog"
          aria-modal="true"
          aria-label="Artwork preview"
          onClick={() => setLightboxArtwork(null)}
        >
          <div
            className="relative flex h-full max-h-[min(820px,100%)] w-full max-w-5xl flex-col overflow-hidden rounded-lg border border-white/12 bg-[#0a0807] shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setLightboxArtwork(null)}
              className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-black/55 text-white transition-colors hover:bg-black/75"
              aria-label="Close artwork preview"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>

            <div className="min-h-0 flex-1 bg-black">
              <img
                src={lightboxArtwork.imageUrl}
                alt={`Artwork by ${lightboxArtwork.participantName}`}
                className="h-full w-full object-contain"
              />
            </div>

            <div className="flex flex-wrap items-end justify-between gap-3 border-t border-white/10 bg-[#0f0c0a] px-4 py-3">
              <div className="min-w-0">
                <div className="truncate text-sm font-medium text-white">
                  {lightboxArtwork.artistProfile?.displayName || lightboxArtwork.participantName}
                </div>
                {(lightboxArtwork.medium || lightboxArtwork.modelName || lightboxArtwork.modelProfile) && (
                  <div className="mt-1 truncate text-xs text-white/62">
                    {lightboxArtwork.medium || 'Artwork'}
                    {(lightboxArtwork.modelName || lightboxArtwork.modelProfile) && (
                      <>
                        {' · Model: '}
                        {lightboxArtwork.modelName || lightboxArtwork.modelProfile?.displayName}
                      </>
                    )}
                  </div>
                )}
              </div>

              {canPinArtwork && (
                <button
                  type="button"
                  onClick={() => {
                    onPinArtwork(lightboxArtwork);
                    setLightboxArtwork(null);
                  }}
                  className="btn btn-sm"
                >
                  Spotlight
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
